// ============================================================================
// ROLE PERMISSION SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { rolePermissionEndpoints } from '../endpoints/rolePermission.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ID,
} from '../types';
import { transformPaginatedResponse } from '../types/common.types';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionMerged,
  RolePermissionFilterParams,
} from '../types/rolePermission.types';

class RolePermissionService {
  async getAll(params?: RolePermissionFilterParams): Promise<PaginatedResponse<RolePermission>> {
    const endpoint = params
      ? buildEndpointWithQuery(rolePermissionEndpoints.list(), params)
      : rolePermissionEndpoints.list();
    const response = await get(endpoint);
    return transformPaginatedResponse<RolePermission>(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<RolePermission>> {
    const response = await get<ApiResponse<RolePermission>>(rolePermissionEndpoints.getById(id));
    return response.data;
  }

  async getByRoles(roles: string): Promise<ApiResponse<RolePermissionMerged>> {
    const response = await get<ApiResponse<RolePermissionMerged>>(rolePermissionEndpoints.byRoles(roles));
    return response.data;
  }

  async create(payload: CreateRolePermissionRequest): Promise<ApiResponse<RolePermission>> {
    const response = await post<ApiResponse<RolePermission>>(rolePermissionEndpoints.create(), payload);
    return response.data;
  }

  async update(id: ID, payload: UpdateRolePermissionRequest): Promise<ApiResponse<RolePermission>> {
    const response = await put<ApiResponse<RolePermission>>(rolePermissionEndpoints.update(id), payload);
    return response.data;
  }

  async delete(id: ID): Promise<ApiResponse<void>> {
    const response = await del<ApiResponse<void>>(rolePermissionEndpoints.delete(id));
    return response.data;
  }
}

export const rolePermissionService = new RolePermissionService();
export default rolePermissionService;
